import { createFile, DataStream, Endianness, type MP4BoxBuffer } from "mp4box";
import { ArrayBufferTarget, Muxer } from "mp4-muxer";

export interface Segment {
  /** Seconds, kept. */
  start: number;
  end: number;
}

interface RawSample {
  data: Uint8Array;
  cts: number;
  duration: number;
  timescale: number;
  is_sync: boolean;
}

interface Demuxed {
  duration: number;
  video: {
    samples: RawSample[];
    codec: string;
    width: number;
    height: number;
    description: Uint8Array;
    bitrate: number;
    fps: number;
  } | null;
  audio: {
    samples: RawSample[];
    codec: string;
    sampleRate: number;
    channels: number;
    description: Uint8Array | null;
  } | null;
}

/** Reads the samples and codec descriptions out of a recorded MP4. */
async function demux(file: Blob): Promise<Demuxed> {
  const buffer = (await file.arrayBuffer()) as unknown as MP4BoxBuffer;
  buffer.fileStart = 0;
  const iso = createFile();
  const collected = new Map<number, RawSample[]>();
  let info: Record<string, any> | null = null;

  const ready = Promise.withResolvers<Record<string, any>>();
  iso.onReady = (parsed: Record<string, any>) => {
    info = parsed;
    for (const track of parsed.tracks as Record<string, any>[]) {
      collected.set(track.id, []);
      iso.setExtractionOptions(track.id, null, { nbSamples: 2_000_000 });
    }
    iso.start();
    ready.resolve(parsed);
  };
  iso.onSamples = (id: number, _user: unknown, list: unknown[]) => {
    const bucket = collected.get(id);
    if (!bucket) return;
    for (const entry of list) {
      const sample = entry as RawSample & { data?: Uint8Array };
      if (!sample.data) continue;
      bucket.push(sample as RawSample);
    }
  };

  const failed = Promise.withResolvers<never>();
  iso.onError = (message: string) => failed.reject(new Error(`This file could not be read: ${message}`));
  iso.appendBuffer(buffer);
  const parsed = await Promise.race([
    ready.promise,
    failed.promise,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error("This file could not be read in time.")), 30_000)),
  ]);
  iso.flush();
  await new Promise((resolve) => setTimeout(resolve, 150));

  const videoTrack = (parsed.videoTracks as Record<string, any>[])[0];
  const audioTrack = (parsed.audioTracks as Record<string, any>[])[0];
  let duration = 0;
  const result: Demuxed = { duration: 0, video: null, audio: null };

  if (videoTrack) {
    const samples = collected.get(videoTrack.id) ?? [];
    const last = samples[samples.length - 1];
    const totalBytes = samples.reduce((sum, sample) => sum + sample.data.length, 0);
    const span = last ? (last.cts + last.duration) / last.timescale : 0;
    duration = Math.max(duration, span);

    const trak = iso.getTrackById(videoTrack.id) as Record<string, any>;
    const entry = trak.mdia.minf.stbl.stsd.entries[0];
    let description = new Uint8Array();
    if (entry.avcC) {
      const stream = new DataStream(undefined, 0, Endianness.BIG_ENDIAN);
      entry.avcC.write(stream);
      description = new Uint8Array(stream.buffer, 8);
    }
    result.video = {
      samples,
      codec: videoTrack.codec,
      width: videoTrack.video?.width ?? entry.width,
      height: videoTrack.video?.height ?? entry.height,
      description,
      bitrate: span > 0 ? Math.round((totalBytes * 8) / span) : 4_000_000,
      fps: videoTrack.video?.fps ?? 30,
    };
  }

  if (audioTrack) {
    const samples = collected.get(audioTrack.id) ?? [];
    const last = samples[samples.length - 1];
    if (last) duration = Math.max(duration, (last.cts + last.duration) / last.timescale);
    const trak = iso.getTrackById(audioTrack.id) as Record<string, any>;
    const entry = trak.mdia.minf.stbl.stsd.entries[0];
    result.audio = {
      samples,
      codec: audioTrack.codec,
      sampleRate: audioTrack.audio?.sample_rate ?? 48_000,
      channels: audioTrack.audio?.channel_count ?? 2,
      description: audioSpecificConfig(entry, audioTrack),
    };
  }

  result.duration = duration;
  return result;
}

/** DecoderSpecificInfo out of the esds box, or an AAC-LC config built by hand. */
function audioSpecificConfig(entry: Record<string, any>, track: Record<string, any>): Uint8Array | null {
  try {
    const esds = entry.esds;
    const descriptors = esds?.esd?.descs?.[0]?.descs ?? [];
    for (const descriptor of descriptors) {
      if (descriptor.tag === 0x05 && descriptor.data) {
        return new Uint8Array(descriptor.data);
      }
    }
  } catch {
    // Fall through to the computed config.
  }
  const sampleRate = track.audio?.sample_rate ?? 48_000;
  const channels = track.audio?.channel_count ?? 2;
  const rates = [96_000, 88_200, 64_000, 48_000, 44_100, 32_000, 24_000, 22_050, 16_000, 12_000, 11_025, 8_000];
  const index = Math.max(0, rates.indexOf(sampleRate));
  const value = (2 << 11) | (index << 7) | (channels << 3);
  return new Uint8Array([(value >> 8) & 0xff, value & 0xff]);
}

/** Maps an original time to the output timeline, or null when it is cut away. */
function retime(seconds: number, segments: Segment[], offsets: number[]): number | null {
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]!;
    if (seconds >= segment.start && seconds < segment.end) {
      return offsets[i]! + (seconds - segment.start);
    }
  }
  return null;
}

function normalizeSegments(segments: Segment[], duration: number): Segment[] {
  const clamped = segments
    .map((segment) => ({
      start: Math.max(0, Math.min(segment.start, duration)),
      end: Math.max(0, Math.min(segment.end, duration)),
    }))
    .filter((segment) => segment.end - segment.start > 0.02)
    .sort((a, b) => a.start - b.start);
  const merged: Segment[] = [];
  for (const segment of clamped) {
    const last = merged[merged.length - 1];
    if (last && segment.start <= last.end + 0.001) last.end = Math.max(last.end, segment.end);
    else merged.push({ ...segment });
  }
  return merged;
}

/**
 * Keeps the source profile but raises the level to one that covers the frame
 * size: a baseline 3.1 string copied from a 720p take cannot encode 1440p.
 */
function avcCodecFor(codec: string, width: number, height: number): string {
  const match = /^avc1\.([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(codec);
  if (!match) return codec;
  const profile = match[1]!;
  const constraints = match[2]!;
  const macroblocks = Math.ceil(width / 16) * Math.ceil(height / 16);
  // MaxFS per level: 4.0 = 8192 MBs (covers 1080p), 4.1 = 8704, 5.0 = 22080
  // (covers 1440p), 5.1 = 36864 (covers 4K), 5.2 = 36864.
  let level = "28";
  if (macroblocks > 8192) level = "29";
  if (macroblocks > 8704) level = "32";
  if (macroblocks > 22080) level = "33";
  if (macroblocks > 36864) level = "34";
  return `avc1.${profile}${constraints}${level}`;
}

export interface TrimResult {
  blob: Blob;
  extension: "mp4";
  mime: string;
  durationMs: number;
}

/**
 * Cuts a take down to the kept segments. Video is decoded and re-encoded with
 * WebCodecs (hardware), audio samples are copied as they are; nothing plays
 * back in real time, so a five-minute take trims in seconds.
 */
export async function renderTrim(
  file: Blob,
  segments: Segment[],
  onProgress: (fraction: number, label: string) => void,
): Promise<TrimResult> {
  onProgress(0.02, "Reading the take…");
  const source = await demux(file);
  const duration = source.duration;
  const kept = normalizeSegments(segments, duration);
  if (kept.length === 0) throw new Error("Nothing is left after those cuts.");
  if (!source.video) throw new Error("This file has no video track.");

  const offsets: number[] = [];
  let cursor = 0;
  for (const segment of kept) {
    offsets.push(cursor);
    cursor += segment.end - segment.start;
  }
  const outputDuration = cursor;

  const target = new ArrayBufferTarget();
  const muxer = new Muxer({
    target,
    video: {
      codec: "avc" as const,
      width: source.video.width,
      height: source.video.height,
      frameRate: Math.round(source.video.fps),
    },
    ...(source.audio
      ? {
          audio: {
            codec: "aac" as const,
            sampleRate: source.audio.sampleRate,
            numberOfChannels: source.audio.channels,
          },
        }
      : {}),
    firstTimestampBehavior: "offset",
    fastStart: "in-memory",
  });

  let firstAudio = true;
  let encodedFrames = 0;
  let encoderFailure: string | null = null;

  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      muxer.addVideoChunk(chunk, meta, chunk.timestamp);
    },
    error: (error) => {
      encoderFailure = error instanceof Error ? error.message : String(error);
      console.error("trim: video encoder", error);
    },
  });
  encoder.configure({
    codec: avcCodecFor(source.video.codec, source.video.width, source.video.height),
    width: source.video.width,
    height: source.video.height,
    bitrate: Math.max(source.video.bitrate, 2_000_000),
    framerate: Math.max(1, Math.round(source.video.fps)),
    latencyMode: "quality",
    hardwareAcceleration: "prefer-hardware",
    avc: { format: "avc" },
  });

  const videoTotal = source.video.samples.length;
  let decoded = 0;
  let decodeFailure: string | null = null;

  const decoder = new VideoDecoder({
    output: (frame) => {
      const seconds = frame.timestamp / 1_000_000;
      const mapped = retime(seconds, kept, offsets);
      if (mapped === null) {
        frame.close();
        return;
      }
      const at = Math.max(0, Math.round(mapped * 1_000_000));
      try {
        const keyFrame = encodedFrames === 0 || isSegmentStart(seconds, kept);
        encoder.encode(
          new VideoFrame(frame, { timestamp: at, duration: frame.duration ?? undefined }),
          { keyFrame },
        );
        encodedFrames += 1;
      } catch (error) {
        decodeFailure ??= error instanceof Error ? error.message : String(error);
      } finally {
        frame.close();
      }
      if (videoTotal > 0 && decoded % 12 === 0) {
        onProgress(0.1 + 0.75 * (decoded / videoTotal), `Cutting ${decoded}/${videoTotal} frames…`);
      }
    },
    error: (error) => {
      decodeFailure ??= error instanceof Error ? error.message : String(error);
    },
  });
  decoder.configure({
    codec: source.video.codec,
    codedWidth: source.video.width,
    codedHeight: source.video.height,
    description: source.video.description.length > 0 ? source.video.description : undefined,
    hardwareAcceleration: "prefer-hardware",
  });

  for (const sample of source.video.samples) {
    const timestamp = Math.round((sample.cts / sample.timescale) * 1_000_000);
    decoder.decode(
      new EncodedVideoChunk({
        type: sample.is_sync ? "key" : "delta",
        timestamp,
        duration: Math.round((sample.duration / sample.timescale) * 1_000_000),
        data: sample.data,
      }),
    );
    decoded += 1;
    if (decoded % 60 === 0) await new Promise((resolve) => setTimeout(resolve, 0));
  }
  try {
    await decoder.flush();
    if (encoderFailure) throw new Error(`The encoder stopped: ${encoderFailure}`);
    try {
      await encoder.flush();
    } catch (error) {
      throw new Error(
        `The encoder stopped: ${encoderFailure ?? (error instanceof Error ? error.message : String(error))}`,
      );
    }
  } finally {
    // Hardware codec sessions are scarce; a failed edit must not hold them.
    try {
      decoder.close();
    } catch {
      // Already closed by the error path.
    }
    try {
      encoder.close();
    } catch {
      // Already closed by the error path.
    }
  }

  if (encodedFrames === 0) {
    throw new Error(
      decodeFailure
        ? `Nothing could be decoded: ${decodeFailure}`
        : "No frames fell inside the kept sections.",
    );
  }

  // Audio is copied, not re-encoded: cutting between AAC frames is clean.
  if (source.audio && source.audio.samples.length > 0) {
    onProgress(0.9, "Keeping the sound…");
    const description = source.audio.description ?? undefined;
    for (const sample of source.audio.samples) {
      const seconds = sample.cts / sample.timescale;
      const mapped = retime(seconds, kept, offsets);
      if (mapped === null) continue;
      muxer.addAudioChunkRaw(
        sample.data,
        "key",
        Math.max(0, Math.round(mapped * 1_000_000)),
        Math.round((sample.duration / sample.timescale) * 1_000_000),
        firstAudio
          ? {
              decoderConfig: {
                codec: source.audio.codec,
                sampleRate: source.audio.sampleRate,
                numberOfChannels: source.audio.channels,
                description,
              },
            }
          : undefined,
      );
      firstAudio = false;
    }
  }

  onProgress(0.96, "Writing the file…");
  muxer.finalize();
  const blob = new Blob([target.buffer], { type: "video/mp4" });
  onProgress(1, "Done");
  return {
    blob,
    extension: "mp4",
    mime: "video/mp4",
    durationMs: Math.round(outputDuration * 1000),
  };
}

function isSegmentStart(seconds: number, segments: Segment[]): boolean {
  return segments.some((segment) => Math.abs(segment.start - seconds) < 0.001);
}
